import React from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { useAuth } from "./hooks/useAuth";
import Layout from "./components/Layout";
import LoginPage from "./pages/LoginPage";
import DashboardPage from "./pages/DashboardPage";
import UsersPage from "./pages/UsersPage";
import ShoppersPage from "./pages/ShoppersPage";
import PropertiesPage from "./pages/PropertiesPage";
import ManagementCompaniesPage from "./pages/ManagementCompaniesPage";
import OrdersPage from "./pages/OrdersPage";
import CallsPage from "./pages/CallsPage";
import CallDetailPage from "./pages/CallDetailPage";
import SurveyPage from "./pages/SurveyPage";
import ReportsPage from "./pages/ReportsPage";
import ManagementDashboardPage from "./pages/ManagementDashboardPage";
import AnnouncementsPage from "./pages/AnnouncementsPage";
import PurchaseCallPage from "./pages/PurchaseCallPage";
import ContractSpecsPage from "./pages/ContractSpecsPage";
import SurveyBuilderPage from "./pages/SurveyBuilderPage";
import ProfilePage from "./pages/ProfilePage";
import ImportedCallsPage from "./pages/ImportedCallsPage";
import UserPasswordsPage from "./pages/UserPasswordsPage";
import UserActivityPage from "./pages/UserActivityPage";
import CallSetupPage from "./pages/CallSetupPage";

const ADMIN_ROLES = ["SuperAdmin", "Admin"];
const GUEST_ROLES = ["Guest"];

function App() {
  const { user, loading, login, logout } = useAuth();

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center px-6">
        <div className="surface-panel rise-in max-w-md px-8 py-10 text-center">
          <div className="eyebrow">Shepherd Control</div>
          <h1 className="mt-3 text-3xl font-semibold tracking-tight">Preparing the workspace</h1>
          <p className="page-copy mx-auto mt-4 max-w-sm">
            Loading the latest calls, orders, and account state for your session.
          </p>
        </div>
      </div>
    );
  }

  if (!user) {
    return <LoginPage onLogin={login} />;
  }

  const isAdmin =
    ADMIN_ROLES.includes(user.role?.name || "") || user.is_staff || user.is_superuser;
  const isGuest = GUEST_ROLES.includes(user.role?.name || "");
  const hasReportDashboardAccess = Boolean(user.report_management_company_id);
  const canUseManagementDashboard = isAdmin || isGuest || hasReportDashboardAccess;
  const usePortalHome = !isAdmin && canUseManagementDashboard;

  return (
    <BrowserRouter>
      <Layout user={user} onLogout={logout}>
        <Routes>
          <Route path="/" element={usePortalHome ? <Navigate to="/management-dashboard" /> : <DashboardPage user={user} />} />
          {canUseManagementDashboard && <Route path="/management-dashboard" element={<ManagementDashboardPage user={user} />} />}
          {!isGuest && <Route path="/calls" element={<CallsPage user={user} />} />}
          {!isGuest && <Route path="/calls/:id" element={<CallDetailPage />} />}
          {!isGuest && <Route path="/calls/:id/survey" element={<SurveyPage />} />}
          {!isGuest && <Route path="/orders" element={<OrdersPage />} />}
          {!isGuest && <Route path="/reports" element={<ReportsPage />} />}
          <Route path="/profile" element={<ProfilePage user={user} />} />
          {!isGuest && <Route path="/announcements" element={<AnnouncementsPage user={user} />} />}
          {!isGuest && <Route path="/purchase" element={<PurchaseCallPage user={user} />} />}
          {isAdmin && (
            <>
              <Route path="/users" element={<UsersPage />} />
              <Route path="/shoppers" element={<ShoppersPage />} />
              <Route path="/properties" element={<PropertiesPage />} />
              <Route path="/management-companies" element={<ManagementCompaniesPage />} />
              <Route path="/contract-specs" element={<ContractSpecsPage />} />
              <Route path="/call-setup" element={<CallSetupPage />} />
              <Route path="/survey-builder" element={<SurveyBuilderPage />} />
              <Route path="/imported-calls" element={<ImportedCallsPage />} />
              <Route path="/user-passwords" element={<UserPasswordsPage />} />
              <Route path="/user-activity" element={<UserActivityPage />} />
            </>
          )}
          <Route path="*" element={<Navigate to={isGuest ? "/management-dashboard" : "/"} />} />
        </Routes>
      </Layout>
    </BrowserRouter>
  );
}

export default App;
